/**
 * Survey Builder component for creating human surveys
 * Extends existing question parsing infrastructure
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, Users } from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import SurveyQuestionsPreview from './SurveyQuestionsPreview';
import useQuestionParsingSocket from '../../hooks/useQuestionParsingSocket';
import {
  createSurveyWithQuestions,
  createSurveyGroup,
  type SurveyBlockDefinition,
} from '../../utils/surveyApi';
import { SurveyValidationError, describeReportErrors } from '../../utils/surveyValidation';
import type { RedirectConfig } from './SurveyQuestionsPreview';
import type { ParsedQuestion } from '../../components/survey/types';
import type { SurveyTimerConfig } from './surveyTimer';
import {
  GROUP_COUNT_FIXED_AT_CREATION,
  type SurveyAuthoringSettings,
} from './surveyAuthoringSettings';
import { UI_TEXT } from '../../config/ui-text';
import { useAuth } from '../../context/AuthContext';
import DraftRestoredBanner from '../../shared/draft/DraftRestoredBanner';
import type { DraftDescription } from '../../shared/draft/draftDescription';
import { useDebouncedDraftSave } from '../../shared/draft/useDebouncedDraftSave';
import {
  surveyBuilderDraftStore,
  surveyDraftIsWorthRestoring,
  describeSurveyDraft,
  draftStepFor,
} from './surveyBuilderDraft';

// Validation errors interface
interface ValidationErrors {
  surveyName?: string;
  questions?: string;
}

/** Said when a create fails with no structured reason to quote. */
const CREATE_FAILED_FALLBACK =
  'We could not create this survey. Your questions and settings are still here — please try again.';

const SurveyBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'input' | 'preview' | 'creating'>('input');
  const [surveyName, setSurveyName] = useState<string>('');
  const [surveyTitle, setSurveyTitle] = useState<string>('');
  const [surveyDescription, setSurveyDescription] = useState<string>('');
  const [surveyInstructions, setSurveyInstructions] = useState<string>('');
  const [questions, setQuestions] = useState<string>('');
  const [goal, setGoal] = useState<string>('');
  const [context, setContext] = useState<string>('');
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  /** Why the last create attempt failed, shown above the preview. Null while nothing has failed. */
  const [createError, setCreateError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [groupCount, setGroupCount] = useState<number>(1);
  // Owned by the preview, but held here so it reaches the draft: parsedQuestions carry a
  // block_id that points at these, so restoring one without the other is what produced
  // dangling ids and a survey created against blocks: [].
  const [blocks, setBlocks] = useState<SurveyBlockDefinition[]>([]);
  const [maxResponses, setMaxResponses] = useState<number | null>(null);
  const [redirectConfig, setRedirectConfig] = useState<RedirectConfig | null>(null);
  const [adaptiveProbing, setAdaptiveProbing] = useState<boolean>(false);
  const [surveyLevelProbing, setSurveyLevelProbing] = useState<boolean>(true);
  const [minLinkedInConnections, setMinLinkedInConnections] = useState<number | null>(null);
  // Survey-wide countdown (spec 35). null = no timer, which is also what a cleared toggle sends.
  const [timerConfig, setTimerConfig] = useState<SurveyTimerConfig | null>(null);

  // --- Draft persistence (#403) ---
  const { user } = useAuth();
  const [restoredDraft, setRestoredDraft] = useState<DraftDescription | null>(null);
  const [restoreSettled, setRestoreSettled] = useState(false);

  // Restore once the owning user is known — the key is scoped by user id, so reading
  // before that resolves would read the unscoped key and, on a shared machine, could hand
  // one researcher another's survey.
  useEffect(() => {
    if (restoreSettled || !user?.id) return;
    surveyBuilderDraftStore.setScope(user.id);
    const d = surveyBuilderDraftStore.load();
    /* eslint-disable react-hooks/set-state-in-effect -- restoring React state from
       localStorage once its owning identity is known is what an effect is for. */
    if (!surveyDraftIsWorthRestoring(d)) {
      setRestoreSettled(true);
      return;
    }
    setSurveyName(d.surveyName || '');
    setSurveyTitle(d.surveyTitle || '');
    setSurveyDescription(d.surveyDescription || '');
    setSurveyInstructions(d.surveyInstructions || '');
    setQuestions(d.questions || '');
    setGoal(d.goal || '');
    setContext(d.context || '');
    setParsedQuestions((d.parsedQuestions as ParsedQuestion[]) || []);
    setGroupCount(d.groupCount || 1);
    setBlocks((d.blocks as SurveyBlockDefinition[]) || []);
    setMaxResponses(d.maxResponses ?? null);
    setRedirectConfig((d.redirectConfig as RedirectConfig | null) ?? null);
    setAdaptiveProbing(d.adaptiveProbing ?? false);
    setSurveyLevelProbing(d.surveyLevelProbing ?? true);
    setMinLinkedInConnections(d.minLinkedInConnections ?? null);
    setTimerConfig((d.timerConfig as SurveyTimerConfig | null) ?? null);
    setStep(draftStepFor(d.step));
    setRestoredDraft(describeSurveyDraft(d, Date.now()));
    setRestoreSettled(true);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [user?.id, restoreSettled]);

  // Mirror into the draft as the user types. Never writes an EMPTY draft over a real one:
  // the first render has blank state, and a blind save then would destroy the very survey
  // this exists to protect.
  useDebouncedDraftSave(() => {
    if (!restoreSettled || !user?.id || isCreating) return;
    const candidate = {
      surveyName,
      surveyTitle,
      surveyDescription,
      surveyInstructions,
      questions,
      goal,
      context,
      parsedQuestions,
      blocks,
      maxResponses,
      redirectConfig,
      adaptiveProbing,
      surveyLevelProbing,
      minLinkedInConnections,
      timerConfig,
      groupCount,
      step: draftStepFor(step),
    };
    if (!surveyDraftIsWorthRestoring(candidate)) return;
    surveyBuilderDraftStore.save(candidate);
  }, [
    restoreSettled,
    user?.id,
    isCreating,
    surveyName,
    surveyTitle,
    surveyDescription,
    surveyInstructions,
    questions,
    goal,
    context,
    parsedQuestions,
    blocks,
    maxResponses,
    redirectConfig,
    adaptiveProbing,
    surveyLevelProbing,
    minLinkedInConnections,
    timerConfig,
    groupCount,
    step,
  ]);

  // Closing the tab mid-survey is the case people report; the browser's own prompt is the
  // only thing that can interrupt it. Only while there is something to lose.
  useEffect(() => {
    const hasWork = surveyDraftIsWorthRestoring({
      surveyName,
      surveyTitle,
      surveyDescription,
      surveyInstructions,
      questions,
      goal,
      context,
      parsedQuestions,
      blocks,
      maxResponses,
      redirectConfig,
      adaptiveProbing,
      surveyLevelProbing,
      minLinkedInConnections,
      groupCount,
      step: draftStepFor(step),
    });
    if (!hasWork || isCreating) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [
    surveyName,
    surveyTitle,
    surveyDescription,
    surveyInstructions,
    questions,
    goal,
    context,
    parsedQuestions,
    blocks,
    maxResponses,
    redirectConfig,
    adaptiveProbing,
    surveyLevelProbing,
    minLinkedInConnections,
    groupCount,
    step,
    isCreating,
  ]);

  const discardDraft = useCallback(() => {
    surveyBuilderDraftStore.clear();
    setRestoredDraft(null);
    setSurveyName('');
    setSurveyTitle('');
    setSurveyDescription('');
    setSurveyInstructions('');
    setQuestions('');
    setGoal('');
    setContext('');
    setParsedQuestions([]);
    setGroupCount(1);
    setStep('input');
  }, []);

  const { parsingState, triggerParsing, clearParsingState } = useQuestionParsingSocket({
    onParsingCompleted: (data) => {
      if (data.parsed_questions && data.parsed_questions.length > 0) {
        setParsedQuestions(data.parsed_questions);
        setStep('preview');
      }
    },
    autoConnect: true,
  });

  // Validate form fields
  const validateForm = useCallback((): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!surveyName.trim()) {
      errors.surveyName = 'Survey name is required';
    }

    if (!questions.trim()) {
      errors.questions = 'Please enter at least one question';
    } else {
      // Check if there are valid questions after splitting
      const questionsList = questions
        .split('\n')
        .map((q) => q.trim())
        .filter((q) => q.length > 0);

      if (questionsList.length === 0) {
        errors.questions = 'Please enter at least one valid question';
      }
    }

    return errors;
  }, [surveyName, questions]);

  const handleQuestionsSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setHasAttemptedSubmit(true);

      // Validate form
      const errors = validateForm();
      setValidationErrors(errors);

      // If there are validation errors, don't proceed
      if (Object.keys(errors).length > 0) {
        return;
      }

      // Split questions by line breaks and filter empty lines
      const questionsList = questions
        .split('\n')
        .map((q) => q.trim())
        .filter((q) => q.length > 0);

      try {
        // SurveyBuilder is the human-survey paste-raw-text flow, so the parser
        // needs the human schema (which includes dropdown / image_hotspot /
        // photo_upload + UX-only fields like randomize_choices,
        // enable_ai_followup, display_time_seconds).
        await triggerParsing(questionsList, goal, context, undefined, true, undefined, 'human');
      } catch (error) {
        console.error('Failed to trigger question parsing:', error);
      }
    },
    [questions, goal, context, triggerParsing, validateForm],
  );

  /**
   * Mirror the preview's settings into builder state so the draft carries them.
   *
   * `undefined` means "not supplied" and must leave the value alone; `null` is a real value (a
   * quota removed, a timer switched off). Collapsing the two is how a restored draft loses a
   * setting the author had chosen.
   */
  const absorbSettings = useCallback((settings: SurveyAuthoringSettings) => {
    if (settings.blocks !== undefined) setBlocks(settings.blocks ?? []);
    if (settings.redirect_config !== undefined) setRedirectConfig(settings.redirect_config ?? null);
    if (settings.max_responses !== undefined) setMaxResponses(settings.max_responses ?? null);
    if (settings.adaptive_probing !== undefined) setAdaptiveProbing(settings.adaptive_probing);
    if (settings.min_linkedin_connections !== undefined) {
      setMinLinkedInConnections(settings.min_linkedin_connections ?? null);
    }
    if (settings.survey_level_probing_enabled !== undefined) {
      setSurveyLevelProbing(settings.survey_level_probing_enabled);
    }
    if (settings.timer_config !== undefined) setTimerConfig(settings.timer_config);
  }, []);

  const handleQuestionsUpdated = useCallback(
    (updatedQuestions: ParsedQuestion[], settings: SurveyAuthoringSettings) => {
      setParsedQuestions(updatedQuestions);
      absorbSettings(settings);
    },
    [absorbSettings],
  );

  const handleCreateSurvey = useCallback(
    async (
      finalSurveyName: string,
      finalQuestions: ParsedQuestion[],
      settings: SurveyAuthoringSettings,
    ) => {
      setIsCreating(true);
      setCreateError(null);
      // Keep builder state in step with what the preview just committed, so a draft saved between
      // here and the API call carries the settings the author actually set.
      absorbSettings(settings);

      try {
        // Post-create target differs by kind: a group's own detail route vs. the survey's.
        // Land the author on the thing they just made, not the list they'd have to re-search.
        const metadata = {
          title: surveyTitle.trim() || undefined,
          description: surveyDescription.trim() || undefined,
          instructions: surveyInstructions.trim() || undefined,
        };

        let target: string;
        if (groupCount > 1) {
          const { group_id } = await createSurveyGroup({
            name: finalSurveyName,
            group_count: groupCount,
            questions: finalQuestions,
            blocks: settings.blocks ?? undefined,
            ...metadata,
            redirect_config: settings.redirect_config,
            max_responses_per_group: settings.max_responses,
          });
          target = `/survey-groups/${group_id}`;
        } else {
          const { survey_id } = await createSurveyWithQuestions(
            finalSurveyName,
            finalQuestions,
            metadata,
            settings,
          );
          target = `/surveys/${survey_id}`;
        }

        // The survey now exists server-side; a draft that outlived it would offer to
        // "restore" a survey the user has already created.
        surveyBuilderDraftStore.clear();
        navigate(target);
      } catch (error) {
        console.error('Failed to create survey:', error);
        // A create that failed saved NOTHING, so the author needs the reason, not a button that
        // quietly stops spinning. A structured 422 carries the reason; anything else gets the
        // generic line. Never routed into the typed-confirmation modal — see ruling R4.
        setCreateError(
          error instanceof SurveyValidationError
            ? describeReportErrors(error.report, CREATE_FAILED_FALLBACK)
            : CREATE_FAILED_FALLBACK,
        );
        setIsCreating(false);
      }
    },
    [navigate, surveyTitle, surveyDescription, surveyInstructions, groupCount, absorbSettings],
  );

  const handleGoBack = useCallback(() => {
    if (step === 'preview') {
      setStep('input');
      setParsedQuestions([]);
      clearParsingState();
    } else {
      navigate('/surveys');
    }
  }, [step, navigate, clearParsingState]);

  if (step === 'preview') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {restoredDraft && (
            <DraftRestoredBanner
              description={restoredDraft}
              onStartFresh={discardDraft}
              onDismiss={() => setRestoredDraft(null)}
            />
          )}
          {createError && (
            <div
              role="alert"
              className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800"
            >
              {createError}
            </div>
          )}
          <SurveyQuestionsPreview
            parsedQuestions={parsedQuestions}
            surveyName={surveyName}
            surface="create"
            initialBlocks={blocks}
            initialMaxResponses={maxResponses}
            initialRedirectConfig={redirectConfig}
            initialAdaptiveProbing={adaptiveProbing}
            initialSurveyLevelProbing={surveyLevelProbing}
            initialMinLinkedInConnections={minLinkedInConnections}
            initialTimerConfig={timerConfig}
            onQuestionsUpdated={handleQuestionsUpdated}
            onCreateSurvey={handleCreateSurvey}
            onGoBack={handleGoBack}
            isLoading={isCreating}
            groupCount={groupCount}
          />
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm p-6">
            {restoredDraft && (
              <DraftRestoredBanner
                description={restoredDraft}
                onStartFresh={discardDraft}
                onDismiss={() => setRestoredDraft(null)}
              />
            )}
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGoBack}
                icon={<ArrowLeft className="w-4 h-4 mr-1" />}
              >
                {UI_TEXT.HUMAN_SURVEY.TITLES.BACK_TO_SURVEYS}
              </Button>
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-2">
                <FileText className="w-5 h-5 text-blue-500 mr-2" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {UI_TEXT.HUMAN_SURVEY.TITLES.CREATE_SURVEY}
                </h1>
              </div>
              <p className="text-gray-600">{UI_TEXT.HUMAN_SURVEY.DESCRIPTIONS.MAIN}</p>
            </div>

            <form onSubmit={handleQuestionsSubmit} className="space-y-6">
              {/* Survey Name Input */}
              <div>
                <label
                  htmlFor="surveyName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {UI_TEXT.HUMAN_SURVEY.LABELS.SURVEY_NAME} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="surveyName"
                  value={surveyName}
                  onChange={(e) => {
                    setSurveyName(e.target.value);
                    // Clear error when user starts typing
                    if (validationErrors.surveyName && e.target.value.trim()) {
                      setValidationErrors((prev) => ({ ...prev, surveyName: undefined }));
                    }
                  }}
                  placeholder={UI_TEXT.HUMAN_SURVEY.PLACEHOLDERS.SURVEY_NAME}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                    hasAttemptedSubmit && validationErrors.surveyName
                      ? 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {hasAttemptedSubmit && validationErrors.surveyName ? (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.surveyName}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">
                    Internal identifier for organizing your surveys
                  </p>
                )}
              </div>

              {/* Survey Title Input */}
              <div>
                <label
                  htmlFor="surveyTitle"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Survey Title (Optional)
                </label>
                <input
                  type="text"
                  id="surveyTitle"
                  value={surveyTitle}
                  onChange={(e) => setSurveyTitle(e.target.value)}
                  placeholder="Title shown to survey participants"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Customer-facing title displayed on the welcome screen
                </p>
              </div>

              {/* Survey Description Input */}
              <div>
                <label
                  htmlFor="surveyDescription"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Survey Description (Optional)
                </label>
                <textarea
                  id="surveyDescription"
                  value={surveyDescription}
                  onChange={(e) => setSurveyDescription(e.target.value)}
                  placeholder="Brief description of what this survey is about"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Explain the purpose and context of the survey to participants
                </p>
              </div>

              {/* Survey Instructions Input */}
              <div>
                <label
                  htmlFor="surveyInstructions"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Survey Instructions (Optional)
                </label>
                <textarea
                  id="surveyInstructions"
                  value={surveyInstructions}
                  onChange={(e) => setSurveyInstructions(e.target.value)}
                  placeholder="Instructions for participants on how to complete the survey"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Provide guidance on how to answer questions and what to expect
                </p>
              </div>

              {/* Between-Subject Groups */}
              <div>
                <label
                  htmlFor="groupCount"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  <Users className="w-4 h-4 inline mr-1" />
                  {UI_TEXT.HUMAN_SURVEY.LABELS.GROUP_COUNT} (Optional)
                </label>
                <input
                  type="number"
                  id="groupCount"
                  value={groupCount}
                  min={1}
                  max={26}
                  onChange={(e) =>
                    setGroupCount(Math.max(1, Math.min(26, parseInt(e.target.value) || 1)))
                  }
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {groupCount > 1
                    ? `${groupCount} groups (A–${String.fromCharCode(64 + groupCount)}) will be created. Participants are evenly distributed across groups.`
                    : 'Set to 2 or more to enable between-subject group design.'}
                </p>
                {/* Said BEFORE the choice is committed, in the same words the edit surfaces use to
                  explain the lock afterwards. This is the one screen where the decision is open. */}
                <p className="mt-1 text-sm text-amber-700">{GROUP_COUNT_FIXED_AT_CREATION}</p>
              </div>

              {/* Questions Input */}
              <div>
                <label htmlFor="questions" className="block text-sm font-medium text-gray-700 mb-2">
                  {UI_TEXT.HUMAN_SURVEY.LABELS.QUESTIONS.replace(' *', '')}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="questions"
                  value={questions}
                  onChange={(e) => {
                    setQuestions(e.target.value);
                    // Clear error when user starts typing
                    if (validationErrors.questions && e.target.value.trim()) {
                      setValidationErrors((prev) => ({ ...prev, questions: undefined }));
                    }
                  }}
                  placeholder={UI_TEXT.HUMAN_SURVEY.PLACEHOLDERS.QUESTIONS}
                  className={`w-full px-3 py-3 border rounded-lg resize-none focus:ring-2 focus:border-transparent transition-colors ${
                    hasAttemptedSubmit && validationErrors.questions
                      ? 'border-red-500 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  rows={12}
                />
                {hasAttemptedSubmit && validationErrors.questions ? (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {validationErrors.questions}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">
                    {UI_TEXT.HUMAN_SURVEY.DESCRIPTIONS.QUESTIONS_HELP}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <div
                  className="relative group"
                  title={
                    !surveyName.trim() || !questions.trim()
                      ? 'Fill all required fields (*) to continue'
                      : ''
                  }
                >
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={parsingState.isParsing}
                    icon={<Plus className="w-4 h-4" />}
                  >
                    {parsingState.isParsing
                      ? UI_TEXT.HUMAN_SURVEY.BUTTONS.PROCESSING
                      : UI_TEXT.HUMAN_SURVEY.BUTTONS.PARSE_QUESTIONS}
                  </Button>
                  {/* Tooltip for disabled state hint */}
                  {(!surveyName.trim() || !questions.trim()) && !parsingState.isParsing && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      Fill all required fields (*) to continue
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  )}
                </div>
              </div>
            </form>

            {/* Parsing Progress */}
            {parsingState.isParsing && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">
                    {UI_TEXT.HUMAN_SURVEY.PROGRESS.PROCESSING_TITLE}
                  </span>
                  <span className="text-sm text-blue-700">{parsingState.progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${parsingState.progress}%` }}
                  ></div>
                </div>
                {parsingState.currentStep && (
                  <p className="mt-2 text-sm text-blue-700">{parsingState.currentStep}</p>
                )}
              </div>
            )}

            {/* Error Display */}
            {parsingState.error && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-red-900 mb-1">
                  {UI_TEXT.HUMAN_SURVEY.PROGRESS.PARSING_FAILED}
                </h4>
                <p className="text-sm text-red-700">{parsingState.error}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearParsingState}
                  className="mt-2 text-red-700 hover:text-red-800"
                >
                  {UI_TEXT.HUMAN_SURVEY.BUTTONS.TRY_AGAIN}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SurveyBuilder;
